<script setup lang="ts">
import { requiredValidator } from "@/@core/utils/validators";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import { toast } from "vue3-toastify";
import { VForm } from "vuetify/components/VForm";

const props = defineProps<{
  isDialogOpen: boolean;
  email: string;
  password: string | number;
}>();

interface Emit {
  (e: "closeDialog"): void;
}

// Emits
const emit = defineEmits<Emit>();

// refs
const verifyStepVForm = ref<VForm>();
const verificationCode = ref<string>();
const errors = ref<Record<string, string | undefined>>({
  code: "",
});

// stores
const userStore = useUserStore();
const userRoleStore = useUserRole();
const { currentLocation } = storeToRefs(userRoleStore);
const { getUserRolePermission } = userRoleStore;

// router
const router = useRouter();

/**
 * Handles the validation process for two-factor authentication.
 * It retrieves the user's record, verifies the entered verification code,
 * and updates the user's data accordingly.
 *
 * @returns {Promise<void>}
 */
const handleValidation = async () => {
  try {
    // Validate the form
    let { valid } = await verifyStepVForm.value?.validate();
    if (valid) {
      // Authenticate the user with provided email and password
      const response = await pb
        .collection("users")
        .authWithPassword(props.email.toLowerCase(), props.password);
      const userData = response.record;

      // Get the current user's record
      const record = await pb.collection("users").getOne(userData.id);

      // Get the verification code from the user's data
      const currVerificationCode = record.twoFACode;

      // Verify the entered verification code
      if (currVerificationCode == verificationCode.value) {
        // Clear any previous error messages
        errors.value.code = "";

        // Update the user's data
        userData.lastLogin = moment().toISOString();
        userData.status = "Active";
        userData.twoFACode = '';
        // TODO Confirm this works
        // userData.verified = true;
        userData.emailVisibility = true;

        // Update the user's record in the database
        await pb.collection("users").update(userData.id, userData);

        // Store the user's email in local storage
        localStorage.setItem("email", response.record?.email);

        // Update the current location in the user store
        currentLocation.value = response.record?.homeLocation;
        userStore.currentLocation = response.record?.homeLocation;

        // Fetch the user's role permissions
        await getUserRolePermission();

        // Navigate to the home page
        await router.push("/");
        window.location = window.location
      } else {
        // Display an error message if the verification code is incorrect
        errors.value.code = "Verification Code is Wrong!!";
        // toast.error("Verification Code is Wrong!!", {
        //   position: toast.POSITION.BOTTOM_RIGHT,
        // });
      }
    }
  } catch (error) {
    // Log any errors that occur during the process
  }
};

/**
 * Handles the resend of a verification code to the user's email.
 * This function is called when the user clicks the "Resend" link in the two-factor authentication dialog.
 * It retrieves the user's record, sets the user's status and two-factor code to prepare for a new verification code,
 * and updates the user's data in the database.
 *
 * @returns {Promise<void>} - Returns a promise that resolves when the operation is completed successfully.
 * If an error occurs during the process, it will be logged to the console.
 */
const handleCodeResend = async () => {
  try {
    // Authenticate the user with provided email and password
    const response = await pb
      .collection("users")
      .authWithPassword(props.email.toLowerCase(), props.password);
    const userData = response.record;

    // Set the user status and code for the code to be generated again
    userData.status = "Validating";
    userData.twoFACode = '';

    // Update the user's record in the database
    await pb.collection("users").update(userData.id, userData);

    // Display a success message to the user
    toast.info("New verification code has been sent 👍", {
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  } catch (error) {
    // Log any errors that occur during the process
  }
};
</script>

<template>
  <div class="text-center pa-4">
    <VDialog :model-value="props.isDialogOpen" max-width="400" persistent>
      <VCard>
        <VCardTitle class="text-center">Verify Your Identity</VCardTitle>
        <VCardText class="py-2"> we have sent an email to: </VCardText>
        <VForm ref="verifyStepVForm" class="px-4">
          <VRow>
            <VCol cols="12">
              <VTextField v-model="props.email" disabled />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="verificationCode"
                :rules="[requiredValidator]"
                :error-messages="errors.code"
                label="Enter the code"
              />
              &nbsp; &nbsp; Remember to check spam/junk folders
              <p class="mt-2 text-end">
                Did not receive a code?
                <span
                  class="text-primary cursor-pointer text-decoration-underline"
                  @click="handleCodeResend"
                  >Resend</span
                >
              </p>
            </VCol>
          </VRow>
        </VForm>
        <VDivider class="py-2" />
        <VCardActions>
          <VSpacer />
          <VBtn variant="flat" @click="handleValidation"> Verify </VBtn>
          <VBtn color="secondary" @click="$emit('closeDialog')"> Close </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
